/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import isEmpty from 'lodash.isempty';
import {translate} from '@ion/core/src/locale/locale';
import {ASSET_DETAILS_CONTENT_WIDTH} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import OpenModal from '@ion/dls/src/components/buttons/OpenModal';
import {PrimaryButton} from '@ion/dls/src/components/buttons/PrimaryButton';
import {IonDialog} from '@ion/dls/src/components/dialogs/IonDialog';
import {Hidden} from '@ion/dls/src/layout/Hidden';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {
  LineChartDisplay,
  LineChartDisplayProps
} from './LineChartDisplay';
import NoSensorData from './NoSensorData';
import {SensorAccordion} from './SensorAccordion';

export interface ChartsAccordionProps extends LineChartDisplayProps {
  title: string;
  // true only for the Asset Details Drawer's own SC sensor graphs - passed
  // through to SensorAccordion to gate its current theme section card ...
  isAssetDetailsCard?: boolean;
}

const useStyles = makeStyles(() => ({
  container : {
    height        : 300,
    maxWidth      : 500,
    paddingBottom : '24px'
  },
  expandContainer : {
    height          : 500,
    display         : 'flex',
    backgroundColor : dlsColor('white'),
    overflow        : 'hidden'
  },
  chartContainer : {
    height : 260,
    width  : 'inherit'
  },
  expandChartContainer : {
    padding : 16,
    height  : 420,
    width   : '100%'
  },
  noData : {
    padding : '0 15px'
  },
  openModalIcon : {
    padding        : '0 15px',
    display        : 'flex',
    justifyContent : 'flex-end'
  },
  // current theme drops the legacy maxWidth/paddingBottom so the card's
  // content fills its content box, up to 424px - the body's own 16px flex
  // gap (`AssetDetailsSectionAccordion`) replaces the legacy paddingBottom.
  // The box is narrower than 424px wherever the drawer's scrollbar takes up
  // width of its own (Windows), so a fixed 424px ran past it and cut off the
  // reading's time and the chart's right edge - it fills what there is ...
  currentContainer : {
    boxSizing     : 'border-box',
    maxWidth      : ASSET_DETAILS_CONTENT_WIDTH,
    minWidth      : 0,
    paddingBottom : 0,
    width         : '100%'
  },
  currentNoData : {
    padding : 0
  },
  currentOpenModalIcon : {
    padding : 0
  }
}));

/**
 * ChartsAccordion is a component used for displaying SC Device sensor data.
 *
 * ```typescript
 *  import {ChartsAccordion} from '@/ion-suite/src/routes/tracking/scDetails/reports';
 *
 *   <ChartsAccordion noDataLabel={'No data available'}
 *     title={'Humidity'}
 *     data={{
 *       id   : 'Humidity',
 *       data : getDataForRange(queryData?.reports?.accumulatedResults?.humidity?.data, range)
 *     }}
 *     loading={isLoading},
 *     xScale={xScale}
 *     yScale={yScale}
 *     axisBottom={axisBottom}
 *     axisLeft={axisLeft}
 *     customToolTip={ChartsTooltip}/>
 * ```
 */
export const ChartsAccordion = (props: ChartsAccordionProps) => {
  const classes = useStyles();
  const isCurrent = isCurrentTheme();
  const [isShowExpandModal, setIsShowExpandModal] = useState(false);
  const {data, title, loading, isAssetDetailsCard} = props;
  const {data: values} = data || {};

  const onShowExpandModal = useCallback(() => {
    setIsShowExpandModal(true);
  }, []);

  const onCloseExpandModal = useCallback(() => {
    setIsShowExpandModal(false);
  }, []);

  const lineChartDisplay = useMemo(() => <LineChartDisplay {...props} />, [props]);

  return (
    <SensorAccordion title={title}
      isLoading={loading}
      isAssetDetailsCard={isAssetDetailsCard}>
      {!isEmpty(values) ? (
        <div className={themedClassName(classes.container, classes.currentContainer, isCurrent)}>
          <div className={classes.chartContainer}>
            {lineChartDisplay}
          </div>
        </div>
      ) : (
        <div className={themedClassName(classes.noData, classes.currentNoData, isCurrent)}>
          <NoSensorData sensorType={title} iconSize={32} />
        </div>
      )}
      {!isEmpty(values) && (
        <Hidden show='desktop'>
          <div className={themedClassName(classes.openModalIcon, classes.currentOpenModalIcon, isCurrent)}>
            <OpenModal handleOpenModal={onShowExpandModal} />
          </div>
        </Hidden>
      )}
      {isShowExpandModal && (
        <IonDialog title={translate(`tracking:accordionTitle.${title}`)}
          onClose={onCloseExpandModal}
          actions={(
            <PrimaryButton onClick={onCloseExpandModal}>
              {translate('common:close')}
            </PrimaryButton>
          )}
          shouldRemoveDefaultContentPadding
          shouldSupportContentHeight
          shouldCloseOnBackdropClick
          open>
          <div className={classes.expandContainer}>
            <div className={classes.expandChartContainer}>
              {lineChartDisplay}
            </div>
          </div>
        </IonDialog>
      )}
    </SensorAccordion>
  );
};
